"use client";

import { useEffect, useMemo } from "react";
import { formatCurrency, CartItem } from "@/utils/cartUtils";
import Link from "next/link";
import CTALink from "@/components/CTA/variants/CtaLink";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import { getCorrelationID } from "@/utils/tagular/helpers";
import {
  createBannerElementViewEventData,
  createBannerCheckoutClickEventData,
  createBannerProductViewEventData,
  createBannerConversionEventData,
} from "@/components/Navigation/AbandonedCartTest/utils/eventHelpers";

export interface AbandonedCartSuggestionCardProps {
  productId: string;
  productUrl: string;
  subject: string;
  productName: string;
  displayPrice: number;
  originalPrice?: number;
  checkoutUrl: string;
  ctaText?: string;
  headerText?: string;
  subheaderText?: string;
  pageLocation?: string; // e.g., "Search", "Learn"
}

export function AbandonedCartSuggestionCard({
  productId,
  productUrl,
  subject,
  productName,
  displayPrice,
  originalPrice,
  checkoutUrl,
  ctaText,
  headerText,
  subheaderText,
  pageLocation = "page",
}: AbandonedCartSuggestionCardProps) {
  const hasDiscount = originalPrice && originalPrice > displayPrice;

  // Generate correlationId once using useMemo to ensure single initialization
  // This approach guarantees it only runs once, even in Strict Mode
  const correlationId = useMemo(() => getCorrelationID(), []);

  // Fire tracking events when banner is viewed
  useEffect(() => {
    // Element Viewed event for banner
    const bannerViewEvent = makeEvent(
      createBannerElementViewEventData(
        pageLocation,
        productName,
        correlationId,
      ),
    );
    bannerViewEvent(EVENTS.ElementViewed)();

    // Product Viewed event for banner product
    const cartItem: CartItem = {
      id: productId,
      courseName: productName,
      price: displayPrice,
      originalPrice: originalPrice || displayPrice,
      totalPrice: displayPrice,
      quantity: 1,
      type: "course",
      courseKey: productId,
      courseRunKey: productId,
      brand: "edX",
    };

    const productViewEvent = makeEvent(
      createBannerProductViewEventData(
        cartItem,
        0,
        pageLocation,
        "edX",
        correlationId,
      ),
    );
    productViewEvent(EVENTS.ProductViewed)();
  }, [
    productId,
    productName,
    displayPrice,
    originalPrice,
    pageLocation,
    correlationId,
  ]);

  // Handle checkout button click tracking
  const handleCheckoutClick = () => {
    // Fire Element Clicked event for banner "Go to checkout"
    const bannerClickEvent = makeEvent(
      createBannerCheckoutClickEventData(
        pageLocation,
        productName,
        correlationId,
      ),
    );
    bannerClickEvent(EVENTS.ElementClicked)();

    // Fire Conversion Tracked event when user clicks checkout
    const cartItem: CartItem = {
      id: productId,
      courseName: productName,
      price: displayPrice,
      originalPrice: originalPrice || displayPrice,
      totalPrice: displayPrice,
      quantity: 1,
      type: "course",
      courseKey: productId,
      courseRunKey: productId,
      brand: "edX",
    };

    const conversionEvent = createBannerConversionEventData(
      [cartItem],
      "edX",
      correlationId,
    );

    const event = makeEvent<ConversionTracked, EVENTS>(conversionEvent);
    event<ConversionTracked>(EVENTS.ConversionTracked)();
  };

  return (
    <div
      className="flex flex-col md:flex-row items-center gap-4 rounded-xl text-white p-8 bg-[#00262B] w-full max-w-7xl"
      role="article"
      aria-labelledby={`suggestion-title-${productId}`}
    >
      <div className="flex-1">
        <h3
          id={`suggestion-title-${productId}`}
          className="mb-2 mt-0 text-left text-white font-bold not-italic leading-[1.2] [font-family:var(--Font-Styles-Font-Family,Inter)] text-[36px]"
        >
          {headerText || "Still interested?"}
        </h3>
        <p className="text-white font-normal leading-[1.4] not-italic [font-family:var(--Font-Styles-Font-Family,Inter)] text-[var(--Type-Sizes-Paragraph-Elm-P4,18px)]">
          {subheaderText ? (
            subheaderText.split("{productTitle}").map((part, index, array) => (
              <span key={index}>
                {part}
                {index < array.length - 1 && (
                  <strong className="text-white">{productName}</strong>
                )}
              </span>
            ))
          ) : (
            <>
              Enroll in <strong className="text-white">{productName}</strong>{" "}
              today!
            </>
          )}
        </p>
      </div>

      <div className="flex flex-col items-center gap-2 flex-shrink-0 w-full md:w-auto">
        <div className="flex items-center gap-2">
          {hasDiscount && (
            <span className="text-white/70 line-through text-sm">
              {formatCurrency(originalPrice)}
            </span>
          )}
          <span className="text-white text-center font-bold leading-[1.4] not-italic [font-family:var(--Font-Styles-Font-Family,Inter)] text-[var(--Type-Sizes-Paragraph-Elm-P4,18px)]">
            {formatCurrency(displayPrice)}
          </span>
        </div>

        <CTALink
          label={ctaText || "Add to cart"}
          url={checkoutUrl}
          type="brand"
          size="default"
          radius="default"
          location="abandoned-cart-suggestions"
          html_id={`abandoned-cart-suggestion-${productId}`}
          className="flex justify-center items-center font-medium transition-colors px-6 py-[10px] gap-2 rounded-full no-underline bg-brand text-brand-foreground shadow border-2 border-brand hover:bg-white hover:text-brand"
          onClick={handleCheckoutClick}
        />

        <Link
          href={productUrl}
          className="text-white/90 hover:text-white underline text-sm text-center"
          aria-label={`See details for ${productName}`}
        >
          See details
        </Link>
      </div>
    </div>
  );
}
